(() => {
  const sorts = { new: 'createTime', reply: 'lastReplyTime', top: 'likeCount', comments: 'commentCount', shares: 'shareCount' };
  const listPayload = (type, state, userId) => ({ type, start: state.page, size: state.size || (type === 'POST' ? 12 : 4), sortBy: type === 'POST' && state.sort === 'top' ? 'favoriteCount' : sorts[state.sort] || 'createTime', sortOrder: 'desc', ...(userId ? { userId: String(userId) } : {}), ...(type === 'QUESTION' && state.category ? { category: state.category } : {}) });
  const readPage = body => {
    if (body?.status !== 'success') throw new Error(body?.message || 'Unable to load content.');
    const data = body.data;
    if (!Array.isArray(data?.list) || !Number.isInteger(data.totalCount) || data.totalCount < 0) throw new Error('Invalid list response.');
    return { list: data.list, totalCount: data.totalCount };
  };
  const safeUrl = value => {
    const text = String(value || '').trim();
    if (/[\\\r\n]/.test(text)) return '';
    if (/^\/(?!\/)/.test(text)) return text;
    try {
      const url = new URL(text);
      if (url.origin === 'http://informationcollection.benyuan1.com' && url.pathname.startsWith('/file/')) url.protocol = 'https:';
      return url.protocol === 'https:' && !url.username && !url.password ? url.href : '';
    } catch { return ''; }
  };
  const mediaUrl = value => safeUrl(String(value || '').trim().startsWith('//') ? `https:${String(value).trim()}` : value);
  const userBadgeNames = { active: 'Active Member', star: 'Community Star', legend: 'Community Legend', favorite: 'Community Favorite', rising: 'Rising Creator', helpful: 'Helpful Creator' };
  const userBadgeKeys = (user = {}) => {
    const keys = [];
    const levelKeys = [];
    const levels = ['active', 'star', 'legend'];
    if (typeof user.points === 'number' && Number.isFinite(user.points) && user.points >= 0) {
      const level = [10, 500, 2000].filter(threshold => user.points >= threshold).length;
      if (level) levelKeys.push(levels[level - 1]);
    } else if (typeof user.levelBadge === 'string') {
      const label = user.levelBadge.trim().toLowerCase();
      const level = levels.find((key, index) => label === userBadgeNames[key].toLowerCase() || label === `lv${index + 1}`);
      if (level) levelKeys.push(level);
    }
    const awards = (Array.isArray(user.award) ? user.award : [user.award]).filter(value => typeof value === 'string').join(' ');
    for (const key of ['favorite', 'rising', 'helpful']) {
      const name = userBadgeNames[key].replace(/ /g, '\\s+');
      if (new RegExp(`(?:^|[\\s_,;，；|])${name}(?=$|[\\s_,;，；|])`, 'i').test(awards)) keys.push(key);
    }
    return [...keys, ...levelKeys];
  };
  const renderUserBadges = (node, user) => {
    const badges = node.querySelector('[data-user-badges]');
    const artwork = node.querySelector('[data-user-badge-art]');
    if (!badges || !artwork) return;
    badges.replaceChildren(...userBadgeKeys(user || {}).map(key => {
      const image = artwork.content.querySelector(`[data-badge-key="${key}"]`).cloneNode(true);
      image.setAttribute('src', image.getAttribute('src').trim());
      image.alt = userBadgeNames[key]; image.title = userBadgeNames[key];
      return image;
    }));
    badges.hidden = !badges.childElementCount;
  };
  const withUserIdentity = (payload, identity) => {
    if (!payload.userId || String(payload.userId) !== String(identity.userId || '')) return payload;
    const result = { ...payload, email: String(identity.email || '').trim() };
    const name = String(identity.displayName || payload.displayName || '').trim();
    if (name) result.displayName = name; else delete result.displayName;
    return result;
  };
  const profiles = new Map();
  const resolveIdentity = async identity => {
    const userId = String(identity.userId || '');
    if (!userId) return identity;
    let saved;
    try {
      const cached = JSON.parse(sessionStorage.getItem(`beetles-profile:${userId}`));
      if (String(cached?.userId) === userId) saved = cached;
    } catch {}
    if (!profiles.has(userId)) {
      const pending = fetch('https://informationcollection.benyuan1.com/api/community/user/info', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }), signal: AbortSignal.timeout(8000)
      }).then(async response => {
        const body = await response.json();
        if (!response.ok || body.status !== 'success' || String(body.data?.userId) !== userId) throw Error('Unable to load profile.');
        return body.data;
      }).catch(() => { profiles.delete(userId); return null; });
      profiles.set(userId, pending);
    }
    const profile = await profiles.get(userId);
    const displayName = [profile?.displayName, saved?.displayName, identity.displayName].find(name => typeof name === 'string' && name.trim());
    const avatar = mediaUrl(profile?.avatar ?? saved?.avatar ?? identity.avatar);
    return { ...identity, displayName: displayName?.trim() || '', avatar,
      ...(profile && Object.prototype.hasOwnProperty.call(profile, 'award') ? { award: profile.award } : {}),
      ...(profile && Object.prototype.hasOwnProperty.call(profile, 'levelBadge') ? { levelBadge: profile.levelBadge } : {}),
      ...(profile && Object.prototype.hasOwnProperty.call(profile, 'points') ? { points: profile.points } : {}),
      ...(Array.isArray(profile?.publicTags) ? { publicTags: profile.publicTags } : {}),
      tags: profile ? (Array.isArray(profile.tags) ? profile.tags : []) : (Array.isArray(identity.tags) ? identity.tags : []) };
  };
  const officialEmailSet = value => new Set(String(value || '').split(/[\s,;，；]+/).map(email => email.trim().toLowerCase()).filter(Boolean));
  const renderOfficialBadge = (node, user, emails) => {
    node.querySelector('.community-feed__official-badge')?.remove();
    const email = typeof user?.email === 'string' ? user.email.trim().toLowerCase() : '';
    if (!email || !emails.has(email)) return;
    const badge = document.createElement('span');
    badge.className = 'community-feed__official-badge';
    badge.textContent = 'Official';
    node.insertBefore(badge, node.querySelector('[data-user-badges]'));
  };
  const userDisplayName = (user, fallback = 'Community member') => {
    const name = typeof user?.displayName === 'string' ? user.displayName.trim() : '';
    if (name) return name;
    const email = typeof user?.email === 'string' ? user.email.trim() : '';
    const at = email.indexOf('@');
    return at > 0 ? email.slice(0, at).trim() || fallback : fallback;
  };
  if (typeof module !== 'undefined') module.exports = { listPayload, readPage, safeUrl, mediaUrl, withUserIdentity, officialEmailSet, userDisplayName, userBadgeKeys };
  const renderProAvatar = (node, user) => {
    const cardAuthor = node.closest('.community-share__author-row,.community-help__meta-author');
    cardAuthor?.querySelector('.community-feed__pro-card-badge')?.remove();
    node.querySelector('.community-feed__pro-badge')?.remove();
    const isPro = Array.isArray(user?.tags) && user.tags.includes('Beetles Pro Membership');
    node.classList.toggle('community-feed__pro-avatar', isPro && !cardAuthor);
    if (!isPro) return;
    const badge = document.createElement('span');
    badge.className = 'community-feed__pro-badge' + (cardAuthor ? ' community-feed__pro-card-badge' : '');
    badge.textContent = 'PRO';
    badge.setAttribute('aria-label', 'PRO member');
    if (cardAuthor) cardAuthor.insertBefore(badge, cardAuthor.querySelector('.community-feed__official-badge, [data-user-badges]'));
    else node.append(badge);
  };
  if (typeof window !== 'undefined') window.CommunityFeedUtils = { readPage, safeUrl, mediaUrl, withUserIdentity, resolveIdentity, renderProAvatar, officialEmailSet, renderOfficialBadge, userDisplayName, userBadgeKeys, renderUserBadges };
  if (typeof document === 'undefined' || window.CommunityFeeds) return;
  document.addEventListener('community:profile-updated', event => {
    const profile = event.detail;
    if (profile?.userId && profile.displayName?.trim()) {
      const key = String(profile.userId);
      const previous = profiles.get(key);
      profiles.set(key, Promise.resolve(previous).then(saved => ({ ...saved, ...profile })));
    }
  });
  const el = (tag, className, text) => {
    const node = document.createElement(tag); if (className) node.className = className;
    if (text !== undefined) node.textContent = text; return node;
  };
  const count = value => Math.max(0, Number(value) || 0);
  const init = root => {
    if (root.dataset.feedReady) return;
    root.dataset.feedReady = 'true';
    const life = new AbortController();
    const userId = root.dataset.customerId;
    const officialEmails = officialEmailSet(root.dataset.officialEmails);
    const states = Object.fromEntries(['POST', 'QUESTION'].map(type => [type, { page: 1, sort: 'new', category: '', serial: 0, productCleanups: [] }]));
    const mobile = matchMedia('(max-width: 767px)');
    const questionSize = () => Math.max(1, Math.min(100, Number(mobile.matches ? root.dataset.questionMobileSize : root.dataset.questionPageSize) || 4));
    states.QUESTION.size = questionSize();
    const query = (name, type) => root.querySelector(`[data-feed-${name}="${type}"]`);
    const api = async (path, payload, signal = life.signal) => {
      const identity = payload.userId && String(payload.userId) === String(userId) ? await resolveIdentity({ userId, email: root.dataset.customerEmail, displayName: root.dataset.customerName }) : {};
      const response = await fetch(`https://informationcollection.benyuan1.com/api/community/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(withUserIdentity(payload, identity)), signal: AbortSignal.any([signal, AbortSignal.timeout(20000)]) });
      const body = await response.json();
      if (!response.ok || body.status !== 'success') throw new Error(body.message || 'Unable to load content.');
      return body;
    };
    const login = () => window.CommunityRequireLogin(root);
    const mediaNode = (media, className, title) => {
      const url = mediaUrl(media?.url); if (!url || !['image', 'video'].includes(media.type)) return null;
      const node = el(media.type === 'video' ? 'video' : 'img', className); node.src = url;
      if (media.type === 'video') { node.controls = true; node.playsInline = true; node.preload = 'metadata'; }
      else { node.alt = title || ''; node.loading = 'lazy'; }
      return node;
    };
    const addImageBackdrop = (container, image) => {
      if (root.classList.contains('monthly-challenge') || image?.tagName !== 'IMG') return;
      container.classList.add('community-feed__image-backdrop');
      container.style.setProperty('--community-card-image', `url(${JSON.stringify(image.src)})`);
    };
    const avatar = (node, user) => {
      node.textContent = userDisplayName(user, '?').slice(0, 1);
      const url = mediaUrl(user?.avatar);
      if (url) { const image = el('img'); image.src = url; image.alt = ''; image.loading = 'lazy'; node.replaceChildren(image); }
      renderProAvatar(node, user);
    };
    const metric = (node, text) => { const icon = node.querySelector('svg'); node.replaceChildren(...(icon ? [icon] : []), document.createTextNode(text)); };
    const shareContent = window.CommunityShareMenu(root, { api, login, userId, el, life });
    const detail = window.CommunityFeedDetail(root, { api, login, userId, el, avatar, mediaNode, mediaUrl, safeUrl, life, shareContent });
    const showDetail = (id, type, preview, options) => detail.open(id, type, preview, options);
    const card = (item, type) => {
      const node = query('template', type).content.firstElementChild.cloneNode(true);
      const post = type === 'POST'; const prefix = post ? 'community-share' : 'community-help';
      if (post) node.classList.add('community-feed__post-card');
      node.dataset.contentId = item.id;
      node.querySelector('h3 a').textContent = item.title || '';
      node.querySelectorAll('h3 a').forEach(link => { const url = new URL(location.href); url.searchParams.set('content', item.id); link.href = url.href; link.addEventListener('click', event => { if (event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return; event.preventDefault(); showDetail(item.id, type, item); }); });
      node.classList.add('community-feed__clickable');
      node.addEventListener('click', event => { if (event.target.closest('a,button,video,input,textarea,select,.community-feed__product-chooser') || window.getSelection()?.toString()) return; showDetail(item.id, type, item); });
      const cover = mediaNode(item.media?.[0], post ? '' : 'community-help__question-image', item.title);
      if (post) {
        const old = node.querySelector('.community-share__media'); const container = el('div', old.className); if (cover) container.append(cover); addImageBackdrop(container, cover); old.replaceWith(container);
        container.append(el('span', 'community-share__occasion-tag', String(item.occasion || '').trim() || 'Just for Fun'));
        node.querySelector('.community-share__author strong').textContent = userDisplayName(item.user);
        node.querySelector('.community-share__author small').textContent = Array.isArray(item.user?.publicTags) ? item.user.publicTags.join(' · ') : '';
        const tags = [item.nailShape, item.nailLength, item.skinTone].filter(Boolean);
        if (tags.length) { const list = el('ul', 'community-share__tags'); tags.forEach(tag => list.append(el('li', '', tag))); node.querySelector('h3').after(list); }
        if (item.content?.trim()) node.querySelector('h3').after(el('p', 'community-feed__excerpt', item.content));
      } else {
        node.querySelector('.community-help__category').textContent = item.category || '';
        node.querySelector('.community-help__excerpt').textContent = item.content || '';
        const old = node.querySelector('.community-help__question-image');
        if (cover?.tagName === 'IMG' && !root.classList.contains('monthly-challenge')) {
          const container = el('div', cover.className);
          cover.className = '';
          container.append(cover);
          addImageBackdrop(container, cover);
          old.replaceWith(container);
        } else if (cover) old.replaceWith(cover); else old.remove();
        node.querySelector('.community-help__meta-author>span:nth-child(2)').textContent = userDisplayName(item.user);
        if (Array.isArray(item.user?.publicTags) && item.user.publicTags.length) node.querySelector('.community-help__meta-author').append(el('span', 'community-feed__public-tags', item.user.publicTags.join(' · ')));
      }
      avatar(node.querySelector(`.${prefix}__avatar`), item.user);
      renderOfficialBadge(node.querySelector(post ? '.community-share__author-row' : '.community-help__meta-author'), item.user, officialEmails);
      renderUserBadges(node, item.user);
      if (item.user?.userId && (item.user.award === undefined || (item.user.points === undefined && item.user.levelBadge === undefined))) {
        resolveIdentity(item.user).then(user => {
          if (!life.signal.aborted && node.isConnected) renderUserBadges(node, user);
        }).catch(() => {});
      }
      const shop = node.querySelector(`.${prefix}__shop`);
      states[type].productCleanups.push(window.CommunityCardProducts(shop, item.products, { el, safeUrl, mediaUrl, life }));
      const like = node.querySelector(post ? '[data-community-action="like"]' : '[data-community-action="helpful"]');
      const share = node.querySelector('[data-community-action="share"]');
      const comments = node.querySelector(post ? '.community-share__metrics>a' : '.community-help__meta-actions>span');
      const syncReaction = () => {
        const active = post ? item.isFavorited : item.isLiked;
        const total = count(post ? item.favoriteCount : item.likeCount);
        metric(like, post ? String(total) : `Helpful (${total})`);
        like.setAttribute('aria-pressed', String(Boolean(active)));
        like.setAttribute('aria-label', post ? `${active ? 'Saved' : 'Save'} (${total})` : `Helpful (${total})`);
      };
      syncReaction();
      metric(share, post ? String(count(item.shareCount)) : `Share (${count(item.shareCount)})`); share.setAttribute('aria-label', 'Share');
      metric(comments, post ? String(count(item.commentCount)) : `${count(item.commentCount)} answers`);
      const commentButton = el('button'); commentButton.type = 'button';
      commentButton.replaceChildren(...comments.childNodes);
      commentButton.setAttribute('aria-label', `${count(item.commentCount)} ${post ? 'comments' : 'answers'}`);
      commentButton.addEventListener('click', () => { if (login()) showDetail(item.id, type, item, { scrollToComments: true }); });
      comments.replaceWith(commentButton);
      like.removeAttribute('data-community-action'); share.removeAttribute('data-community-action');
      const react = async (action, button) => {
        if (!login()) return; button.disabled = true;
        try {
          const { data } = await api(`reaction/${action}`, { userId, contentType: type.toLowerCase(), contentId: item.id, ...(post ? { isFavorited: !item.isFavorited } : { isLiked: !item.isLiked }) });
          Object.assign(item, data);
          syncReaction();
          metric(share, post ? String(count(item.shareCount)) : `Share (${count(item.shareCount)})`);
        } catch (error) { query('status', type).textContent = error.message || 'Unable to complete this action.'; }
        finally { button.disabled = false; }
      };
      like.addEventListener('click', () => react(post ? 'favorite' : 'like', like)); shareContent.bindTrigger(share, item, type, data => { Object.assign(item, data); metric(share, post ? String(count(item.shareCount)) : `Share (${count(item.shareCount)})`); });
      return node;
    };
    const syncControls = type => {
      const state = states[type];
      root.querySelectorAll(type === 'POST' ? '.community-share__sort button' : '.community-help__sort button').forEach((button, index) => {
        const active = (button.dataset.askSort || ['new', 'reply', 'top'][index]) === state.sort; button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active));
      });
      if (type === 'QUESTION') root.querySelectorAll('.community-help__categories button').forEach((button, index) => { const active = (index ? button.textContent.trim() : '') === state.category; button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active)); });
    };
    const questionSkeleton = () => {
      const node = el('div', 'community-feed__skeleton community-feed__question-skeleton');
      node.setAttribute('aria-hidden', 'true');
      const block = name => el('span', `community-feed__skeleton-block community-feed__skeleton-${name}`);
      const identity = el('div', 'community-feed__skeleton-row');
      identity.append(block('avatar'), block('name'), block('action'), block('action'));
      node.append(block('tag'), block('title'), block('line'), block('short'), block('media'), identity);
      return node;
    };
    const postSkeleton = () => {
      const node = el('div', 'community-feed__skeleton community-feed__post-skeleton');
      node.setAttribute('aria-hidden', 'true');
      const block = name => el('span', `community-feed__skeleton-block community-feed__skeleton-${name}`);
      const content = el('div', 'community-feed__post-skeleton-content');
      const author = el('div', 'community-feed__skeleton-row');
      author.append(block('avatar'), block('name'));
      const tags = el('div', 'community-feed__skeleton-row');
      tags.append(block('tag'), block('tag'));
      const actions = el('div', 'community-feed__skeleton-row');
      actions.append(block('action'), block('action'), block('action'));
      content.append(author, block('title'), block('line'), block('short'), tags, block('link'), actions);
      node.append(block('cover'), content);
      return node;
    };
    const load = async (type, { scrollToTop = false } = {}) => {
      const state = states[type]; const serial = ++state.serial; state.controller?.abort(); state.controller = new AbortController();
      const list = query('list', type), status = query('status', type), pages = query('pages', type), retry = query('retry', type);
      state.productCleanups.splice(0).forEach(dispose => dispose());
      syncControls(type);
      list.replaceChildren(...Array.from({ length: type === 'QUESTION' ? Math.min(state.size || 4, 3) : listPayload(type, state, userId).size }, type === 'QUESTION' ? questionSkeleton : postSkeleton));
      pages.replaceChildren(); list.setAttribute('aria-busy', 'true'); status.textContent = 'Loading…'; retry.hidden = true;
      status.classList.add('community-feed__loading-status');
      // Show skeletons in the viewport before waiting for the next page.
      // Waiting until success leaves visitors at the old pagination position.
      if (scrollToTop) list.scrollIntoView({ block: 'start', behavior: 'instant' });
      try {
        const [response] = await Promise.all([
          api('content/list', listPayload(type, state, userId), state.controller.signal),
          new Promise(resolve => setTimeout(resolve, 220))
        ]);
        const result = readPage(response);
        if (serial !== state.serial || !root.isConnected) return;
        const size = listPayload(type, state, userId).size; const totalPages = Math.ceil(result.totalCount / size);
        if (state.page > Math.max(1, totalPages)) { state.page = Math.max(1, totalPages); return load(type, { scrollToTop }); }
        list.replaceChildren(...result.list.map(item => card(item, type)));
        status.textContent = result.list.length ? '' : (type === 'POST' ? 'No posts yet.' : 'No questions found.');
        if (totalPages > 1) {
          const add = (label, page, disabled = false) => { const button = el('button', '', label); button.type = 'button'; button.disabled = disabled; if (label === String(state.page)) button.setAttribute('aria-current', 'page'); button.addEventListener('click', () => { if (state.page === page) return; state.page = page; load(type, { scrollToTop: true }); }); pages.append(button); };
          add('Previous', state.page - 1, state.page === 1);
          const visible = [...new Set([1, state.page - 1, state.page, state.page + 1, totalPages])].filter(page => page > 0 && page <= totalPages).sort((a, b) => a - b);
          visible.forEach((page, index) => { if (index && page - visible[index - 1] > 1) pages.append(el('span', '', '…')); add(String(page), page); });
          add('Next', state.page + 1, state.page === totalPages);
          if (type === 'POST') {
            pages.firstElementChild.textContent = '‹';
            pages.firstElementChild.setAttribute('aria-label', 'Previous');
            pages.lastElementChild.textContent = '›';
            pages.lastElementChild.setAttribute('aria-label', 'Next');
          }
        }
      } catch (error) { if (serial === state.serial && error.name !== 'AbortError') { list.replaceChildren(); status.textContent = error.message || 'Unable to load content.'; retry.hidden = false; } }
      finally { if (serial === state.serial) { list.removeAttribute('aria-busy'); status.classList.remove('community-feed__loading-status'); } }
    };
    const contentId = new URL(location.href).searchParams.get('content');
    if (contentId) showDetail(contentId, 'POST');
    for (const type of ['POST', 'QUESTION']) {
      root.querySelectorAll(type === 'POST' ? '.community-share__sort button' : '.community-help__sort button').forEach((button, index) => button.addEventListener('click', () => { Object.assign(states[type], { sort: button.dataset.askSort || ['new', 'reply', 'top'][index], page: 1 }); load(type); }, { signal: life.signal }));
      query('retry', type).addEventListener('click', () => load(type), { signal: life.signal });
      document.addEventListener(type === 'POST' ? 'community:post-created' : 'community:question-created', () => { Object.assign(states[type], { page: 1, sort: 'new', category: '' }); load(type); }, { signal: life.signal });
      load(type);
    }
    root.querySelectorAll('.community-help__categories button').forEach((button, index) => button.addEventListener('click', () => { Object.assign(states.QUESTION, { category: index ? button.textContent.trim() : '', page: 1 }); load('QUESTION'); }, { signal: life.signal }));
    mobile.addEventListener('change', () => { const size = questionSize(); if (states.QUESTION.size !== size) { Object.assign(states.QUESTION, { size, page: 1 }); load('QUESTION'); } }, { signal: life.signal });
    root.addEventListener('community:close-detail', () => detail.close(), { signal: life.signal });
    root.feedCleanup = () => { life.abort(); Object.values(states).forEach(state => state.controller?.abort()); detail.destroy(); };
  };
  const initAll = () => document.querySelectorAll('[data-community-share]').forEach(init);
  window.CommunityFeeds = { initAll };
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', event => event.target.querySelector('[data-community-share]')?.feedCleanup?.());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
})();
